import { AnyAction, Dispatch, Store } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  ApiAction,
  ApiError,
  ApiRequestPayload,
  CallbackType,
  ExtraAction,
} from '../../reducers/api';
import { onUpdateLoading } from '../../reducers/system';
import { customHttpStatusMessageTransformer } from './utils';
import createAxiosInstance from '../../../utils/api';

const apiMiddleware =
  ({ dispatch }: Store) =>
  (next: Dispatch) =>
  async (action: ApiAction) => {
    if ('api/call' !== action.type) {
      return next(action);
    }

    const {
      onSuccess,
      onStart,
      onError,
      customErrors: customErrorsMap,
      extraActions,
      callbacks,
    } = action.payload;

    const callback = callbacks?.length
      ? callbacks?.find((it) => it.type === CallbackType.SUCCESS)
      : null;

    const callbackError = callbacks?.length
      ? callbacks?.find((it) => it.type === CallbackType.ERROR)
      : null;

    if (onStart) {
      dispatch({ type: onStart, payload: { loading: true } });
    }

    try {
      const response = await fetch(action.payload, dispatch);
      if (response?.data && onSuccess) {
        dispatch({ type: onSuccess, payload: response.data });
      }
      if (extraActions) {
        extraActions.forEach((item: ExtraAction) => {
          if (item.condition(onSuccess)) {
            dispatch({
              type: item.actionName,
              payload: response?.data ? { ...item.payload, ...response.data } : { ...item.payload },
            });
          }
        });
      }
      if (callback && callback?.function.length) {
        callback.function(response);
      } else if (callback) {
        callback.function('Executado com sucesso', 'success');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        error as AxiosError;
        console.error(error, error?.code);
        const message = customHttpStatusMessageTransformer(error, customErrorsMap ?? []);
        dispatch({
          type: onError,
          payload: {
            ...error.response?.data,
            error: {
              message,
              httpStatus: error.code === 'ERR_NETWORK' ? 500 : error.status,
            },
            unavailableBackend: error.code === 'ERR_NETWORK',
          },
        });
        if (callbackError && callbackError.type === CallbackType.ERROR) {
          if (callbackError.full) {
            callbackError.function(error);
          } else {
            callbackError.function(message, 'error');
          }
        }
      } else {
        const unknownErrorMessage = (error as ApiError).message;
        dispatch({ type: onError, payload: { message: unknownErrorMessage } });
        if (callbackError?.full) {
          callbackError.function(error);
        } else {
          if (callbackError) {
            callbackError.function(unknownErrorMessage, 'error');
          }
        }
      }
    } finally {
      dispatch({ type: onStart, payload: { loading: false } });
    }
  };

async function fetch(request: ApiRequestPayload, dispatch: Dispatch<AnyAction>) {
  const axiosInstance = await createAxiosInstance(request?.sessionKey);
  const { url, method, data } = request;
  if (url?.includes('crm')) {
    axiosInstance.defaults.headers['x-session-key'] = 'CRM';
  }
  dispatch(onUpdateLoading({ loading: true }));
  if (method === 'POST') {
    return await axiosInstance.request({
      url,
      method,
      data,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
  return await axiosInstance.request({
    url,
    method,
    data,
  });
}

export default apiMiddleware;
